import React, { useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowRight, FaEnvelope, FaPhone, FaMapMarkerAlt, FaUser, FaClock, FaStore } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchPharmacyDetails, clearError, clearPharmacyDetails } from '../../../redux/Slices/AdminPharmaciesSlice';

const AdminPharmacyDetails = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { pharmacyId } = useParams();
    
    const { pharmacyDetails, detailsLoading, error } = useSelector((state) => state.adminPharmacies);

    useEffect(() => {
        setTitle('تفاصيل الصيدلية');
        dispatch(fetchPharmacyDetails(pharmacyId));
        
        return () => {
            dispatch(clearPharmacyDetails());
        };
    }, [setTitle, dispatch, pharmacyId]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success">نشط</span>;
        }
        return <span className="badge bg-danger">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger">محظور</span>;
        }
        return <span className="badge bg-success">غير محظور</span>;
    };

    if (detailsLoading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل الصيدلية...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!pharmacyDetails) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <p className="text-muted">لا توجد بيانات</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">تفاصيل الصيدلية</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn text-white"
                        onClick={() => navigate('/admin-pharmacies')}
                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                        <FaArrowRight className="mx-2" />
                        العودة للقائمة
                    </motion.button>
                </div>

                {/* Pharmacy Info Card */}
                <div className="card shadow-lg border-0 mb-4">
                    <div className="card-body p-4">
                        <div className="row">
                            {/* Logo and Cover */}
                            <div className="col-md-12 mb-4">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h6 className="text-muted mb-2">اللوجو</h6>
                                        <div style={{ width: '150px', height: '150px', border: '2px dashed #ddd', borderRadius: '10px' }}>
                                            {pharmacyDetails.logo ? (
                                                <img
                                                    src={pharmacyDetails.logo}
                                                    alt="Logo"
                                                    className="w-100 h-100"
                                                    style={{ objectFit: 'cover', borderRadius: '10px' }}
                                                />
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                    لا يوجد لوجو
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <h6 className="text-muted mb-2">الغلاف</h6>
                                        <div style={{ width: '100%', height: '150px', border: '2px dashed #ddd', borderRadius: '10px' }}>
                                            {pharmacyDetails.cover ? (
                                                <img
                                                    src={pharmacyDetails.cover}
                                                    alt="Cover"
                                                    className="w-100 h-100"
                                                    style={{ objectFit: 'cover', borderRadius: '10px' }}
                                                />
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                    لا يوجد غلاف
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* عرض Logo و Cover */}
                            {(pharmacyDetails.logo || pharmacyDetails.cover) && (
                                <div className="col-12 mb-4">
                                    <div className="row">
                                        {/* Logo */}
                                        {pharmacyDetails.logo && pharmacyDetails.logo !== 'logo' && (
                                            <div className="col-md-6 mb-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaStore className="text-primary mx-2" />
                                                    <h6 className="mb-0 text-muted">شعار الصيدلية (Logo)</h6>
                                                </div>
                                                <div style={{
                                                    width: '200px',
                                                    height: '200px',
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <img
                                                        src={pharmacyDetails.logo}
                                                        alt="Pharmacy Logo"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Cover */}
                                        {pharmacyDetails.cover && pharmacyDetails.cover !== 'cover' && (
                                            <div className="col-md-12 mb-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    <h6 className="mb-0 text-muted">🖼️ صورة الغلاف (Cover)</h6>
                                                </div>
                                                <div style={{
                                                    width: '100%',
                                                    maxWidth: '800px',
                                                    height: '300px',
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <img
                                                        src={pharmacyDetails.cover}
                                                        alt="Pharmacy Cover"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <hr className="my-4" />
                                </div>
                            )}

                            {/* Basic Info */}
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaStore className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">اسم الصيدلية</h6>
                                </div>
                                <p className="fw-bold">{pharmacyDetails.market_name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaUser className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">اسم التاجر</h6>
                                </div>
                                <p className="fw-bold">{pharmacyDetails.trader_name || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaPhone className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">رقم الهاتف</h6>
                                </div>
                                <p className="fw-bold">{pharmacyDetails.phone || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaPhone className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">رقم التواصل</h6>
                                </div>
                                <p className="fw-bold">{pharmacyDetails.contact_number || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaEnvelope className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">البريد الإلكتروني</h6>
                                </div>
                                <p className="fw-bold">{pharmacyDetails.email || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaMapMarkerAlt className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">العنوان</h6>
                                </div>
                                <p className="fw-bold">{pharmacyDetails.address || 'غير محدد'}</p>
                            </div>

                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <FaClock className="text-primary mx-2" />
                                    <h6 className="mb-0 text-muted">مواعيد العمل</h6>
                                </div>
                                <p className="fw-bold">
                                    {pharmacyDetails.start_time && pharmacyDetails.end_time 
                                        ? `من ${pharmacyDetails.start_time} إلى ${pharmacyDetails.end_time}`
                                        : 'غير محدد'
                                    }
                                </p>
                            </div>

                            {pharmacyDetails.lat && pharmacyDetails.lng && (
                                <div className="col-md-12 mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        <FaMapMarkerAlt className="text-primary mx-2" />
                                        <h6 className="mb-0 text-muted">الموقع على الخريطة</h6>
                                    </div>
                                    <div style={{ height: '350px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                        <iframe
                                            title="موقع الصيدلية"
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            style={{ border: 0 }}
                                            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAHQSuklfkVa-mATYvbA7odk3_LpnKG-OA&q=${parseFloat(pharmacyDetails.lat).toFixed(7)},${parseFloat(pharmacyDetails.lng).toFixed(7)}&zoom=18&language=ar`}
                                            allowFullScreen>
                                        </iframe>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <small className="text-muted">
                                            الإحداثيات: {parseFloat(pharmacyDetails.lat).toFixed(7)}, {parseFloat(pharmacyDetails.lng).toFixed(7)}
                                        </small>
                                        <a 
                                            href={`https://www.google.com/maps?q=${parseFloat(pharmacyDetails.lat).toFixed(7)},${parseFloat(pharmacyDetails.lng).toFixed(7)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm text-white"
                                            style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                            فتح في Google Maps
                                        </a>
                                    </div>
                                </div>
                            )}

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">حالة النشاط</h6>
                                {getStatusBadge(pharmacyDetails.status)}
                            </div>

                            <div className="col-md-6 mb-3">
                                <h6 className="text-muted mb-2">حالة الحظر</h6>
                                {getBanBadge(pharmacyDetails.ban)}
                            </div>

                            {pharmacyDetails.description && (
                                <div className="col-md-12 mb-3">
                                    <h6 className="text-muted mb-2">الوصف</h6>
                                    <p className="fw-bold">{pharmacyDetails.description}</p>
                                </div>
                            )}

                            {pharmacyDetails.rate_avg && (
                                <div className="col-md-6 mb-3">
                                    <h6 className="text-muted mb-2">التقييم</h6>
                                    <p className="fw-bold">{pharmacyDetails.rate_avg} ⭐</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

              
            </div>
        </div>
    );
};

export default AdminPharmacyDetails;

